import { useState, useCallback } from 'react';
import { notification } from 'antd';
import confetti from 'canvas-confetti';
import {
  getEvaluationAssistantResponse,
  transcribeAudioForAssistant,
  updateChatAssistantDraft,
} from '../../../API/services/screeningService';
import { t } from '../../../i18n/i18n';
import {
  ERROR_STATUSES,
  CONFETTI_CONFIG,
  CHAT_ASSISTANT_DEFAULTS,
} from '../../../constants/chatAssistant.constants';

export const useMessageManagement = (addPendingSavePromise) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [currentOperation, setCurrentOperation] = useState(null);
  const [operationMessage, setOperationMessage] = useState(null);

  const saveDraftData = useCallback(
    async (draftId, userMessage, assistantMessage, extractedData, currentDraftData) => {
      if (!draftId) return;

      const messagesToSave = [
        {
          id: userMessage.id.toString(),
          role: 'user',
          content: userMessage.content,
          createdAt: new Date().toISOString(),
        },
        {
          id: assistantMessage.id.toString(),
          role: 'assistant',
          content: assistantMessage.content,
          createdAt: new Date().toISOString(),
        },
      ];

      const updateData = { messages: messagesToSave };

      if (extractedData.customTestGroup) {
        updateData.customTestGroup = extractedData.customTestGroup;
      }

      if (extractedData.subskills && extractedData.subskills.length > 0) {
        updateData.subskills = extractedData.subskills;
      }

      if (extractedData.evaluationMetadata) {
        updateData.evaluationMetadata = extractedData.evaluationMetadata;

        if (extractedData.evaluationMetadata.name) {
          const defaultDraftNamePattern = /^Chat - \d{1,2}\/\d{1,2}\/\d{4} \d{1,2}:\d{2}/;
          const currentDraftName = currentDraftData?.name;

          if (!currentDraftName || defaultDraftNamePattern.test(currentDraftName)) {
            updateData.name = extractedData.evaluationMetadata.name;
          }
        }
      }

      if (
        extractedData.progressPercentage !== undefined &&
        extractedData.progressPercentage !== null
      ) {
        updateData.percentage = extractedData.progressPercentage;
      }

      if (extractedData.nextAction) {
        updateData.nextAction = extractedData.nextAction;
      }

      try {
        const savePromise = updateChatAssistantDraft(draftId, updateData);
        if (addPendingSavePromise) {
          addPendingSavePromise(savePromise);
        }
        await savePromise;
      } catch (error) {
        notification.error({
          message: t('ERROR'),
          description: error.message,
        });
        throw error;
      }
    },
    [addPendingSavePromise]
  );

  const processAssistantResponse = useCallback(
    async (
      data,
      userMessage,
      userContent,
      isNewChat,
      currentDraftId,
      currentDraftData,
      createNewDraftFn,
      onEvaluationUpdate
    ) => {
      const draftResult = await handleNewDraft(data, isNewChat, currentDraftId, createNewDraftFn);
      const currentThreadId = draftResult.threadId;

      const extractedData = extractAssistantData(data.result.response, data);

      if (extractedData.evaluationReady) {
        setTimeout(() => {
          confetti({
            particleCount: CONFETTI_CONFIG.PARTICLE_COUNT,
            spread: CONFETTI_CONFIG.SPREAD,
            origin: { y: CONFETTI_CONFIG.ORIGIN_Y },
          });
        }, CONFETTI_CONFIG.DELAY);
      }

      const assistantMessage = {
        role: 'assistant',
        content: extractedData.responseText,
        id: Date.now() + 1,
      };
      setMessages((prev) => [...prev, assistantMessage]);

      if (data.result.threadId && !isNewChat) {
        setThreadId(data.result.threadId);
      }

      const finalDraftId = draftResult.draftId || currentDraftId;
      const messageToSave = {
        ...userMessage,
        content: userContent,
      };

      if (onEvaluationUpdate) {
        onEvaluationUpdate(extractedData, finalDraftId, false);
      }

      await saveDraftData(
        finalDraftId,
        messageToSave,
        assistantMessage,
        extractedData,
        currentDraftData
      );

      return {
        extractedData,
        draftId: finalDraftId,
        threadId: currentThreadId,
      };
    },
    [handleNewDraft, extractAssistantData, saveDraftData, setThreadId]
  );

  const extractAssistantData = useCallback((responseText, data) => {
    const extractedData = {
      responseText,
      progressPercentage: data.result.percentage,
      nextAction: data.result.nextAction || null,
      subskills: data.result.subskills || [],
      difficultyConfig: data.result.difficultyConfig || null,
      customTestGroup: data.result.customTestGroup || null,
      evaluationMetadata: data.result.evaluationMetadata || null,
      evaluationReady: data.result?.success === true,
    };

    if (extractedData.customTestGroup) {
      extractedData.difficultyConfig =
        extractedData.customTestGroup.difficultyConfig || extractedData.difficultyConfig;
    }

    return extractedData;
  }, []);

  const handleErrorResponse = useCallback((error) => {
    let errorContent = t('CHAT_ERROR_SENDING_MESSAGE');
    if (error.response?.status === ERROR_STATUSES.RATE_LIMIT) {
      errorContent = t('CHAT_ERROR_RATE_LIMIT');
    } else if (error.response?.status === ERROR_STATUSES.UNAUTHORIZED) {
      errorContent = t('CHAT_ERROR_AUTHENTICATION');
    } else if (error.response?.status >= ERROR_STATUSES.SERVER_ERROR) {
      errorContent = t('CHAT_ERROR_SERVER');
    }
    return errorContent;
  }, []);

  const handleNewDraft = useCallback(
    async (data, isNewChat, currentDraftId, createNewDraftFn) => {
      if (isNewChat && data.result.threadId && !currentDraftId) {
        const newThreadId = data.result.threadId;
        setThreadId(newThreadId);

        const now = new Date();
        const dateTimeString = `${now.toLocaleDateString(
          CHAT_ASSISTANT_DEFAULTS.LANGUAGE
        )} ${now.toLocaleTimeString(CHAT_ASSISTANT_DEFAULTS.LANGUAGE, {
          hour: '2-digit',
          minute: '2-digit',
        })}`;
        const draftName = t('CHAT_DRAFT_NAME').replace('{{date}}', dateTimeString);

        const draftId = await createNewDraftFn(newThreadId, draftName);
        if (draftId) {
          notification.success({
            message: t('CHAT_DRAFT_CREATED'),
            duration: 2,
          });
          return { threadId: newThreadId, draftId };
        }
        return { threadId: newThreadId, draftId: null };
      }
      if (!isNewChat && data.result.threadId) {
        const newThreadId = data.result.threadId;
        if (!threadId) {
          setThreadId(newThreadId);
        }
        return { threadId: newThreadId, draftId: currentDraftId };
      }
      return { threadId, draftId: currentDraftId };
    },
    [threadId]
  );

  const sendMessageInternal = useCallback(
    async (
      content,
      isAudio,
      audioFile,
      currentDraftId,
      currentDraftData,
      createNewDraftFn,
      onEvaluationUpdate
    ) => {
      if (isLoading) return;
      if (!isAudio && !content.trim()) return;

      let currentThreadId = threadId;
      let isNewChat = !currentThreadId && !currentDraftId;

      setIsLoading(true);

      const userMessage = {
        role: 'user',
        content: isAudio ? t('PROCESSING_AUDIO') : content,
        id: Date.now(),
      };
      setMessages((prev) => [...prev, userMessage]);
      if (!isAudio) setInput('');

      try {
        let finalContent = content;
        if (isAudio) {
          const transcriptionData = await transcribeAudioForAssistant(audioFile);
          finalContent = transcriptionData.transcribedText;
          setMessages((prev) =>
            prev.map((msg) => (msg.id === userMessage.id ? { ...msg, content: finalContent } : msg))
          );
        }

        const data = await getEvaluationAssistantResponse(
          finalContent,
          currentThreadId,
          currentDraftId
        );

        const result = await processAssistantResponse(
          data,
          userMessage,
          finalContent,
          isNewChat,
          currentDraftId,
          currentDraftData,
          createNewDraftFn,
          onEvaluationUpdate
        );

        return result;
      } catch (error) {
        if (isAudio) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === userMessage.id
                ? { ...msg, content: t('ERROR_AUDIO_PROCESSING_SHORT') }
                : msg
            )
          );
        }

        const errorMessage = {
          role: 'assistant',
          content: isAudio ? t('ERROR_AUDIO_PROCESSING') : handleErrorResponse(error),
          id: Date.now() + 1,
        };
        setMessages((prev) => [...prev, errorMessage]);
        return null;
      } finally {
        setIsLoading(false);
        if (isAudio) {
          setCurrentOperation(null);
          setOperationMessage(null);
        }
      }
    },
    [threadId, isLoading, handleErrorResponse, processAssistantResponse]
  );

  const sendMessage = useCallback(
    (messageText, currentDraftId, currentDraftData, createNewDraftFn, onEvaluationUpdate) => {
      return sendMessageInternal(
        messageText,
        false,
        null,
        currentDraftId,
        currentDraftData,
        createNewDraftFn,
        onEvaluationUpdate
      );
    },
    [sendMessageInternal]
  );

  const sendAudioMessage = useCallback(
    (audioFile, currentDraftId, currentDraftData, createNewDraftFn, onEvaluationUpdate) => {
      return sendMessageInternal(
        null,
        true,
        audioFile,
        currentDraftId,
        currentDraftData,
        createNewDraftFn,
        onEvaluationUpdate
      );
    },
    [sendMessageInternal]
  );

  const resetMessages = useCallback(() => {
    setMessages([]);
    setInput('');
    setThreadId(null);
    setCurrentOperation(null);
    setOperationMessage(null);
  }, []);

  return {
    messages,
    input,
    isLoading,
    threadId,
    currentOperation,
    operationMessage,
    setMessages,
    setInput,
    setThreadId,
    setCurrentOperation,
    setOperationMessage,
    sendMessage,
    sendAudioMessage,
    resetMessages,
    extractAssistantData,
  };
};
